import { onBeforeUnmount, ref, type Ref } from 'vue'

/**
 * Open/close for anything that hangs off a trigger: a menu, a date picker, a filter pop.
 *
 * Closing is the part that gets forgotten, and all three ways must work — a click outside,
 * Escape, and choosing something. Escape is a **document** listener rather than a handler
 * on the panel, because focus is usually inside a child by then and a handler bound to the
 * panel only fires while the panel itself has focus.
 *
 * Both listeners are removed on close AND on unmount. A popover unmounted while open
 * otherwise leaves a live Escape handler that closes a panel which no longer exists — the
 * same leak the drawer teardown test exists to catch.
 */
export function usePopover(
  root: Ref<HTMLElement | null>,
  onDismiss?: () => void,
): { open: Ref<boolean>; show: () => void; hide: (notify?: boolean) => void; toggle: () => void } {
  const open = ref(false)

  function hide(notify = true): void {
    open.value = false
    document.removeEventListener('pointerdown', onOutside)
    document.removeEventListener('keydown', onKeydown)
    if (notify) onDismiss?.()
  }

  function onOutside(event: PointerEvent): void {
    // `false`: a click elsewhere is the operator moving on, so focus goes where they
    // clicked rather than being yanked back to the trigger.
    if (root.value !== null && !root.value.contains(event.target as Node)) hide(false)
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') hide()
  }

  function show(): void {
    open.value = true
    document.addEventListener('pointerdown', onOutside)
    document.addEventListener('keydown', onKeydown)
  }

  onBeforeUnmount(() => {
    hide(false)
  })

  return {
    open,
    show,
    hide,
    toggle: (): void => {
      if (open.value) hide()
      else show()
    },
  }
}
